// Intent: the AGENT tab (stage A of agent mode) — a read-only "Moi klienci"
// list for a logged-in agent (Żaneta: no laptop on vacation, phone always).
// Visible only when auth-store.isAgent is true; the server independently
// re-verifies agent-ness on every request, so this screen degrades to an
// error state if the flag was stale. Mock: mocki-stitch/tryb_agenta/moi_klienci.

import { router, useFocusEffect } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, RefreshControl, ScrollView, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AgentClient, fetchAgentClientsApi } from '@/api/mobile-api';
import { registerForPush } from '@/notifications/push';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { HeaderAvatar } from '@/components/ui/header-avatar';
import { useAuthStore } from '@/store/auth-store';
import { colors, fonts, radius, spacing } from '@/theme/tokens';
import { formatDate } from '@/utils/format';

type Filter = 'all' | 'withApp';

export default function AgentPanelScreen() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);
  const isAgent = useAuthStore((s) => s.isAgent);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  // Pure admin (no agent row) has no client book — the caretaker list would
  // only 403. The chat entry (all conversations) is their whole panel.
  const showClients = isAgent || !isAdmin;
  const [clients, setClients] = useState<AgentClient[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async () => {
    if (!showClients) {
      setLoaded(true);
      return;
    }
    const res = await fetchAgentClientsApi();
    if (res.ok && res.data) {
      setClients(res.data);
      setError(null);
    } else {
      setError(res.message ?? t('agentMode.loadError'));
    }
    setLoaded(true);
  }, [t, showClients]);

  // Refetch on every focus: the book of clients changes in the CRM, not here.
  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  // Server push (stage C): agents only for now — a client gains nothing from
  // the permission prompt yet, so it fires on the AGENT tab, not app-wide.
  useEffect(() => {
    void registerForPush();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const query = search.trim().toLowerCase();
  const visible = clients.filter((client) => {
    if (filter === 'withApp' && !client.hasApp) return false;
    if (!query) return true;
    return (
      client.name.toLowerCase().includes(query) ||
      (client.phone ?? '').replace(/\s/g, '').includes(query.replace(/\s/g, ''))
    );
  });

  const initialsOf = (name: string) =>
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top']}>
      <ScrollView
        contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {/* Top bar mirrors Start: avatar (menu) + app name */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
          <HeaderAvatar />
          <AppText variant="headlineSm" color={colors.primary} style={{ flex: 1 }}>
            {t('home.appName')}
          </AppText>
        </View>

        <AppText variant="headlineLg">{t('agentMode.title')}</AppText>
        {account?.firstName ? (
          <AppText variant="bodyLg" color={colors.textSecondary}>
            {t('agentMode.greeting', { name: account.firstName })}
          </AppText>
        ) : null}

        {/* Inbox entry: APP-chat conversations of this agent's clients (stage B) */}
        <Pressable
          accessibilityRole="button"
          onPress={() => router.push('/agent-chat' as never)}
          style={({ pressed }) => ({ transform: [{ scale: pressed ? 0.98 : 1 }] })}
        >
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.infoBg }}>
            <Ionicons name="chatbubbles-outline" size={24} color={colors.primary} />
            <AppText variant="headlineSm" style={{ flex: 1 }}>
              {isAdmin ? t('agentMode.chat.entryAdmin') : t('agentMode.chat.entry')}
            </AppText>
            <Ionicons name="chevron-forward" size={18} color={colors.outlineVariant} />
          </Card>
        </Pressable>

        {!showClients ? null : (
        <>
        {/* Search */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: spacing.xs,
            backgroundColor: colors.card,
            borderRadius: radius.button,
            paddingHorizontal: spacing.sm,
            minHeight: 48,
          }}
        >
          <Ionicons name="search-outline" size={20} color={colors.outline} />
          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder={t('agentMode.searchPlaceholder')}
            placeholderTextColor={colors.outline}
            style={{ flex: 1, fontFamily: fonts.medium, fontSize: 16, color: colors.textPrimary }}
            accessibilityLabel={t('agentMode.searchPlaceholder')}
          />
        </View>

        {/* Filter chips: all / has-the-app */}
        <View style={{ flexDirection: 'row', gap: spacing.xs }}>
          {(['all', 'withApp'] as Filter[]).map((key) => (
            <Pressable
              key={key}
              accessibilityRole="button"
              onPress={() => setFilter(key)}
              style={{
                backgroundColor: filter === key ? colors.card : colors.primaryTile,
                borderRadius: radius.full,
                paddingHorizontal: spacing.md,
                paddingVertical: spacing.xs,
              }}
            >
              <AppText variant="labelLg" color={colors.navy}>
                {key === 'all' ? t('agentMode.filterAll') : t('agentMode.filterWithApp')}
              </AppText>
            </Pressable>
          ))}
        </View>

        {error ? (
          <Card style={{ backgroundColor: colors.warningBg }}>
            <AppText variant="bodyLg" color={colors.warning}>
              {error}
            </AppText>
          </Card>
        ) : null}

        {loaded && !error && visible.length === 0 ? (
          <Card>
            <AppText variant="bodyLg" color={colors.textSecondary}>
              {query || filter !== 'all' ? t('agentMode.emptySearch') : t('agentMode.empty')}
            </AppText>
          </Card>
        ) : null}

        {visible.map((client) => (
          <Pressable
            key={client.id}
            accessibilityRole="button"
            // Typed routes regenerate on the next `expo start`; cast like menu.tsx.
            onPress={() => router.push(`/agent-client/${client.id}` as never)}
            style={({ pressed }) => ({ transform: [{ scale: pressed ? 0.98 : 1 }] })}
          >
            <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <View
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: radius.full,
                  backgroundColor: colors.primaryTile,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AppText variant="labelLg" color={colors.navy}>
                  {initialsOf(client.name)}
                </AppText>
              </View>
              <View style={{ flex: 1 }}>
                <AppText variant="headlineSm">{client.name}</AppText>
                <AppText variant="bodyMd" color={colors.textSecondary}>
                  {t('agentMode.policiesCount', { count: client.policiesCount })}
                  {client.lastPolicyEndDate
                    ? ` · ${t('agentMode.lastPolicyEnd', { date: formatDate(client.lastPolicyEndDate) })}`
                    : ''}
                </AppText>
              </View>
              {client.hasApp ? (
                <View
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: radius.full,
                    backgroundColor: colors.success,
                  }}
                  accessibilityLabel={t('agentMode.hasAppBadge')}
                />
              ) : null}
              <Ionicons name="chevron-forward" size={18} color={colors.outlineVariant} />
            </Card>
          </Pressable>
        ))}
        </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
